// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Item classification + small display helpers. Pure so they are unit-tested
 * directly: given a snippet of text, decide whether it is a link, a one-time
 * code, or plain text, and how to preview it.
 */
import type { ItemKind } from './types.ts';

/** True if the whole string is a single http(s) or mailto URL. */
export function isUrl(s: string): boolean {
  const t = s.trim();
  if (!t || /\s/.test(t)) return false;
  if (/^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(t)) return true;
  if (!/^https?:\/\//i.test(t)) return false;
  try {
    const u = new URL(t);
    return !!u.hostname && u.hostname.includes('.');
  } catch {
    return false;
  }
}

/**
 * True if the string looks like a one-time / verification code: a short, compact
 * token of 4–10 characters, all digits, or digits mixed with a few letters and
 * no whitespace (e.g. "492013", "8FQ2-9K"). Pure heuristic used only to offer a
 * prominent one-tap copy.
 */
export function looksLikeCode(s: string): boolean {
  const t = s.trim();
  if (/\s/.test(t)) return false;
  const compact = t.replace(/[\s-]/g, '');
  if (compact.length < 4 || compact.length > 10) return false;
  if (!/^[A-Za-z0-9]+$/.test(compact)) return false;
  const digits = (compact.match(/\d/g) || []).length;
  // All digits, or majority digits (a typical alphanumeric OTP).
  return digits === compact.length || digits / compact.length >= 0.5;
}

/** Classify a text snippet into a display kind. */
export function classifyText(s: string): Exclude<ItemKind, 'image'> {
  if (isUrl(s)) return 'link';
  if (looksLikeCode(s)) return 'code';
  return 'text';
}

/** Truncate a snippet for a compact single-line preview. */
export function previewText(s: string, max = 140): string {
  const oneLine = s.replace(/\s+/g, ' ').trim();
  if (oneLine.length <= max) return oneLine;
  return oneLine.slice(0, max - 1).trimEnd() + '…';
}

/** A short, unguessable item id. */
export function makeItemId(): string {
  const b = new Uint8Array(8);
  globalThis.crypto.getRandomValues(b);
  let out = '';
  for (const x of b) out += x.toString(16).padStart(2, '0');
  return out;
}

/** A sensible filename for a received image lacking one, given its MIME type. */
export function imageFilename(name: string | undefined, mime: string | undefined, at: number): string {
  if (name && name.trim()) return name.trim();
  const ext = mime === 'image/png' ? 'png' : mime === 'image/webp' ? 'webp' : mime === 'image/gif' ? 'gif' : 'jpg';
  return `beam-${at}.${ext}`;
}
